/** 로그인 — 실패 사유는 백엔드가 정한다(계정 열거 방지 generic 401). 프런트는 지어내지 않는다. */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useSession } from '@/auth/SessionProvider'
import { LocaleSwitcher } from '@/i18n/LocaleSwitcher'
import { useTheme } from '@/ui/theme'
import { Moon, Sun } from '@/ui/icons'

export default function LoginPage() {
  const { t } = useTranslation()
  const { session, login } = useSession()
  const { theme, toggle } = useTheme()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (session.authed) return <Navigate to={location.state?.from ?? '/'} replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await login(loginId, password)
      navigate(location.state?.from ?? '/', { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('auth.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-2xl border border-line bg-surface p-8 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <span className="wordmark" role="img" aria-label="L.LINE" />
          <div className="flex items-center gap-1">
            <LocaleSwitcher />
            <button
              type="button"
              className="icon-btn"
              onClick={toggle}
              aria-label={t('nav.theme')}
              title={t('nav.theme')}
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
          </div>
        </div>
        <p className="text-sm text-ink-3">{t('auth.prompt')}</p>
        <label className="block space-y-1">
          <span className="text-sm text-ink-2">{t('auth.username')}</span>
          <input
            className="w-full rounded-xl border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-2">{t('auth.password')}</span>
          <input
            type="password"
            className="w-full rounded-xl border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && <p className="text-sm text-danger">{error}</p>}
        <button type="submit" disabled={busy} className="btn solid w-full justify-center">
          {busy ? t('auth.checking') : t('auth.login')}
        </button>
      </form>
    </div>
  )
}
